import React, { useState, useEffect } from 'react';
import {
  BellIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ClockIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useUI } from '../../contexts/UIContext';
import { useConnection } from '../../contexts/EnhancedConnectionContext';
import { apiRequest } from '../../utils/apiUtils';
import { useInvalidateUnreadNotificationCount } from '../../hooks/useUnreadNotificationCount';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const NotificationsPage = () => {
  const { user } = useAuth();
  const { showNotification, updateUnreadCount, updateBreadcrumbs } = useUI();
  const invalidateUnreadCount = useInvalidateUnreadNotificationCount();
  const { connections } = useConnection();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'
  const [typeFilter, setTypeFilter] = useState('all');

  // Set breadcrumbs
  useEffect(() => {
    updateBreadcrumbs([
      { name: 'Dashboard', href: '/dashboard' },
      { name: 'Notifications', href: '/notifications' }
    ]);
  }, [updateBreadcrumbs]);

  useEffect(() => {
    if (user) {
      // Add a small delay to ensure other page loads complete first
      const timeoutId = setTimeout(() => {
        loadNotifications();
      }, 200);
      
      return () => clearTimeout(timeoutId);
    } else {
      setLoading(false);
    }
  }, [user, filter]);

  const loadNotifications = async (retryCount = 0) => {
    try {
      setLoading(true);
      let url = '/notifications?limit=50';
      if (filter === 'unread') {
        url += '&unread_only=true';
      }
      
      
      // Add a small delay to prevent simultaneous requests
      await new Promise(resolve => setTimeout(resolve, 100 + (retryCount * 200)));
      
      const response = await apiRequest(url, {
        timeout: 30000, // 30 second timeout
        skipThrottle: false
      });
      
      if (response && response.success) {
        setNotifications(response.notifications || []);
        setUnreadCount(response.unread_count || 0);
      } else {
        console.error('NotificationsPage: API returned unsuccessful response:', response);
        // Don't show error notification for unsuccessful API responses - could be temporary
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('NotificationsPage: Error loading notifications:', error);
      console.error('NotificationsPage: Error details:', error.response?.data || error.message);
      
      // Handle different types of errors more gracefully
      if (error.response?.status === 401) {
        // Don't show notification for auth errors - let the auth system handle it
      } else if ((error.message?.includes('ConnectionTerminated') || 
                  error.message?.includes('timeout') ||
                  error.message?.includes('Network Error')) && retryCount < 2) {
        // Retry up to 2 times for connection errors
        setTimeout(() => {
          loadNotifications(retryCount + 1);
        }, 1000 * (retryCount + 1)); // Exponential backoff
        return;
      } else if (error.message?.includes('ConnectionTerminated') || 
                 error.message?.includes('timeout') ||
                 error.message?.includes('Network Error')) {
        // For connection errors after retries, set empty state but don't show error notification
        setNotifications([]);
        setUnreadCount(0);
      } else {
        // Only show error notification for unexpected errors
        showNotification('Failed to load notifications. Please try refreshing the page.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const response = await apiRequest(`/notifications/${notificationId}/read`, {
        method: 'POST'
      });

      if (response.success) {
        setNotifications(prev =>
          prev.map(notification =>
            notification.id === notificationId
              ? { ...notification, read: true, read_at: new Date().toISOString() }
              : notification
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
        // Update global badge count optimistically + invalidate the React
        // Query cache so the next Header re-render doesn't push a stale
        // count back into UIContext.
        updateUnreadCount(Math.max(0, unreadCount - 1));
        invalidateUnreadCount();
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
      showNotification('Failed to mark notification as read', 'error');
    }
  };

  const markAllAsRead = async () => {
    try {
      const response = await apiRequest('/notifications/read-all', {
        method: 'POST'
      });

      if (response.success) {
        setNotifications(prev =>
          prev.map(notification => ({
            ...notification,
            read: true,
            read_at: new Date().toISOString()
          }))
        );
        setUnreadCount(0);
        // Update global badge count optimistically + invalidate cache
        updateUnreadCount(0);
        invalidateUnreadCount();
        showNotification('All notifications marked as read', 'success');
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      showNotification('Failed to mark all notifications as read', 'error');
    }
  };

  const getNotificationIcon = (type, severity) => {
    const iconClass = "h-6 w-6";
    
    switch (type) {
      case 'validation_failure':
      case 'automation_failure':
        return <ExclamationTriangleIcon className={`${iconClass} text-danger-500`} />;
      case 'issue_created':
      case 'issue_updated':
        return <ExclamationTriangleIcon className={`${iconClass} text-warning-500`} />;
      case 'schema_change':
        return <InformationCircleIcon className={`${iconClass} text-primary-500`} />;
      case 'automation_completion':
        return <CheckIcon className={`${iconClass} text-accent-500`} />;
      default:
        return <BellIcon className={`${iconClass} text-secondary-500`} />;
    }
  };

  const getNotificationColor = (type, severity) => {
    if (severity === 'high') return 'border-l-danger-500 bg-danger-50';
    if (severity === 'medium') return 'border-l-warning-500 bg-warning-50';
    if (severity === 'low') return 'border-l-primary-500 bg-primary-50';
    
    switch (type) {
      case 'validation_failure':
      case 'automation_failure':
        return 'border-l-danger-500 bg-danger-50';
      case 'issue_created':
      case 'issue_updated':
        return 'border-l-warning-500 bg-warning-50';
      case 'schema_change':
        return 'border-l-primary-500 bg-primary-50';
      case 'automation_completion':
        return 'border-l-accent-500 bg-accent-50';
      default:
        return 'border-l-secondary-500 bg-secondary-50';
    }
  };

  const formatNotificationTime = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch (error) {
      return 'Unknown time';
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark as read if not already read
    if (!notification.read) {
      markAsRead(notification.id);
    }

    // Handle navigation based on notification type and metadata
    const { metadata } = notification;
    
    switch (notification.type) {
      case 'validation_failure':
        if (metadata.table_name) {
          window.location.href = `/validations?table=${metadata.table_name}`;
        } else {
          window.location.href = '/validations';
        }
        break;
      case 'issue_created':
      case 'issue_updated':
        if (metadata.issue_id) {
          window.location.href = `/issues/${metadata.issue_id}`;
        } else {
          window.location.href = '/issues';
        }
        break;
      case 'schema_change':
        if (metadata.table_name) {
          window.location.href = `/data-explorer/${metadata.table_name}`;
        } else {
          window.location.href = '/analytics/schema-changes';
        }
        break;
      case 'automation':
        // Handle automation notifications based on their content
        if (notification.title && notification.title.includes('Validation Failures')) {
          // For validation failure notifications, go to validations page
          if (metadata.table_name) {
            window.location.href = `/validations?table=${metadata.table_name}`;
          } else {
            window.location.href = '/validations';
          }
        } else if (notification.title && notification.title.includes('Schema Changes')) {
          // For schema change notifications, go to schema changes page
          if (metadata.table_name) {
            window.location.href = `/analytics/schema-changes/${metadata.connection_id}`;
          } else {
            window.location.href = '/analytics/schema-changes';
          }
        } else if (notification.title && notification.title.includes('Job Failed')) {
          // For automation job failures, go to automation page
          window.location.href = '/automation';
        } else {
          // For other automation notifications, go to relevant page based on metadata
          if (metadata.connection_id) {
            // Look up connection to get its slug for human-readable URL
            const conn = connections.find(c => c.id === metadata.connection_id);
            window.location.href = `/connections/${conn?.slug || metadata.connection_id}`;
          } else {
            window.location.href = '/dashboard';
          }
        }
        break;
      default:
        break;
    }
  };

  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'unread' && notification.read) return false;
    if (filter === 'read' && !notification.read) return false;
    if (typeFilter !== 'all' && notification.type !== typeFilter) return false;
    return true;
  });

  const notificationTypes = [...new Set(notifications.map(n => n.type))];

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-secondary-900 flex items-center">
              <BellIcon className="h-8 w-8 mr-3" />
              Notifications
              {unreadCount > 0 && (
                <span className="ml-3 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                  {unreadCount} unread
                </span>
              )}
            </h1>
            <p className="mt-1 text-secondary-600">
              Stay updated with validation failures, issues, and system changes
            </p>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
            >
              <CheckIcon className="h-4 w-4 mr-2" />
              Mark all read
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap gap-4 items-center">
        <div className="flex items-center space-x-2">
          <FunnelIcon className="h-5 w-5 text-secondary-400" />
          <span className="text-sm font-medium text-secondary-700">Filter:</span>
        </div>
        
        <div className="flex space-x-1">
          {['all', 'unread', 'read'].map((filterOption) => (
            <button
              key={filterOption}
              onClick={() => setFilter(filterOption)}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                filter === filterOption
                  ? 'bg-primary-100 text-primary-800'
                  : 'text-secondary-600 hover:bg-secondary-100'
              }`}
            >
              {filterOption.charAt(0).toUpperCase() + filterOption.slice(1)}
            </button>
          ))}
        </div>

        {notificationTypes.length > 1 && (
          <div className="flex space-x-1">
            <button
              onClick={() => setTypeFilter('all')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                typeFilter === 'all'
                  ? 'bg-primary-100 text-primary-800'
                  : 'text-secondary-600 hover:bg-secondary-100'
              }`}
            >
              All Types
            </button>
            {notificationTypes.map((type) => (
              <button
                key={type}
                onClick={() => setTypeFilter(type)}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  typeFilter === type
                    ? 'bg-primary-100 text-primary-800'
                    : 'text-secondary-600 hover:bg-secondary-100'
                }`}
              >
                {type.replace('_', ' ')}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="bg-white rounded-lg shadow">
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <BellIcon className="h-12 w-12 text-secondary-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 mb-2">
              {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
            </h3>
            <p className="text-secondary-500">
              {filter === 'unread' 
                ? 'All caught up! Check back later for new updates.'
                : 'You\'ll see notifications for validation failures, issues, and schema changes here'
              }
            </p>
          </div>
        ) : (
          <div className="divide-y divide-secondary-100">
            {filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`
                  px-6 py-4 border-l-4 cursor-pointer transition-colors duration-150
                  ${getNotificationColor(notification.type, notification.severity)}
                  ${!notification.read ? 'hover:bg-opacity-75' : 'opacity-75 hover:opacity-90'}
                `}
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 mt-1">
                    {getNotificationIcon(notification.type, notification.severity)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className={`text-base font-medium text-secondary-900 ${!notification.read ? 'font-semibold' : ''}`}>
                          {notification.title}
                        </p>
                        <p className="text-secondary-600 mt-1">
                          {notification.message}
                        </p>
                        <div className="flex items-center mt-3 space-x-4">
                          <div className="flex items-center space-x-1">
                            <ClockIcon className="h-4 w-4 text-secondary-400" />
                            <span className="text-sm text-secondary-500">
                              {formatNotificationTime(notification.created_at)}
                            </span>
                          </div>
                          {notification.severity && (
                            <span className={`
                              inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                              ${notification.severity === 'high' ? 'bg-danger-100 text-danger-800' : ''}
                              ${notification.severity === 'medium' ? 'bg-warning-100 text-warning-800' : ''}
                              ${notification.severity === 'low' ? 'bg-primary-100 text-primary-800' : ''}
                            `}>
                              {notification.severity} priority
                            </span>
                          )}
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-800">
                            {notification.type.replace('_', ' ')}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center">
                        {!notification.read && (
                          <div className="w-3 h-3 bg-primary-500 rounded-full mr-2"></div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;